import type { components } from '@sportik/api-coach/schema';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/services/api-client';

type Invitation = components['schemas']['Invitation'];

/**
 * The one definition of this read: the links addressed to one person that can
 * still be claimed. The public promotional link is not an invitation to a
 * client and has no row here.
 */
export function invitationsOptions() {
  return api.queryOptions('get', '/invitations', {
    params: { query: { kind: ['individual'], state: ['active'] } },
  });
}

/** Every variant of the invitations read: what a write to a link, or an accepted request, invalidates. */
export function invitationsKeyPrefix() {
  return invitationsOptions().queryKey.slice(0, 2);
}

/** The coach's pending invitations. */
export function useInvitations() {
  return useQuery(invitationsOptions());
}

function useInvitationsRefresh() {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({ queryKey: invitationsKeyPrefix() });
}

type NewInvitation = {
  /** Made by the caller and reused on a retry, so a retry is not a second link. */
  id: Invitation['id'];
  /** Left out, the coach's saved default is copied; empty, the link carries none. */
  message?: string;
  /** Left out, the link is addressed to nobody and nothing is sent. */
  recipientEmail?: string;
};

/** Creates a single-use link for one person, emailed when an address is given. */
export function useCreateInvitation() {
  return useMutation({
    mutationFn: async ({ id, message, recipientEmail }: NewInvitation) => {
      const { data } = await api.client.PUT('/invitations/{invitationId}', {
        params: { path: { invitationId: id } },
        body: { kind: 'individual', version: 0, message, recipientEmail },
      });
      return data;
    },
    onSettled: useInvitationsRefresh(),
  });
}

/**
 * Rewords a link nobody has claimed yet: the same link, with a new message or,
 * when empty, none. Answers with the link as the API now holds it.
 */
export function useChangeInvitationMessage() {
  return useMutation({
    mutationFn: async ({
      id,
      version,
      message,
    }: Pick<Invitation, 'id' | 'version'> & { message: string }) => {
      const { data } = await api.client.PATCH('/invitations/{invitationId}', {
        params: { path: { invitationId: id } },
        // The contract takes a merge patch, not the client's default JSON.
        headers: { 'Content-Type': 'application/merge-patch+json' },
        body: { message, version },
      });
      return data;
    },
    onSettled: useInvitationsRefresh(),
  });
}

/** Withdraws a link: nobody new can claim it. */
export function useRevokeInvitation() {
  return useMutation({
    mutationFn: ({ id, version }: Pick<Invitation, 'id' | 'version'>) =>
      api.client.POST('/invitations/{invitationId}/revoke', {
        params: { path: { invitationId: id } },
        body: { version },
      }),
    onSettled: useInvitationsRefresh(),
  });
}
